import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, LineChart, Line, YAxis, Tooltip } from 'recharts';
import { useAppState } from '../../context/AppStateContext';
import { SEND_DOMAINS } from '../../services/insightsDataService';
import {
  EhcpTargetService,
  EhcpTarget,
  GapAnalysis,
  GapTargetResult,
} from '../../services/ehcpTargetService';
import './charts.scss';

const AREA_LABEL: Record<string, string> = SEND_DOMAINS.reduce((a, d) => { a[d.key] = d.label; return a; }, {} as Record<string, string>);
const AREA_COLOUR: Record<string, string> = SEND_DOMAINS.reduce((a, d) => { a[d.key] = d.colour; return a; }, {} as Record<string, string>);

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: '2-digit' });
}

type Verdict = 'strong' | 'some' | 'gap';
function verdictFor(score: number | null, threshold: number): Verdict {
  if (score == null || score < threshold) return 'gap';
  return score >= threshold + 0.05 ? 'strong' : 'some';
}
const VERDICT_META: Record<Verdict, { label: string; colour: string }> = {
  strong: { label: 'Strong evidence', colour: '#26923f' },
  some:   { label: 'Some evidence',   colour: '#e8a92e' },
  gap:    { label: 'Gap — little evidence', colour: '#b8302f' },
};

interface Props {
  childId: number;
  childName: string | null;
  fromDate: string;
  toDate: string;
}

interface FormState { target_text: string; area: string; is_active: boolean; }
const EMPTY_FORM: FormState = { target_text: '', area: '', is_active: true };

const TargetGapAnalysis: React.FC<Props> = ({ childId, childName, fromDate, toDate }) => {
  const { userRoleIds, roles } = useAppState();
  const canManage = userRoleIds.includes(roles.Reporting) || userRoleIds.includes(roles.Admin);

  const [targets, setTargets] = useState<EhcpTarget[]>([]);
  const [gap, setGap] = useState<GapAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // null = not editing; 'new' = add form; number = editing that target.
  const [editing, setEditing] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, g] = await Promise.all([
        EhcpTargetService.list(childId),
        EhcpTargetService.gapAnalysis(childId, fromDate, toDate),
      ]);
      setTargets(t);
      setGap(g);
    } catch (err) {
      console.error('Failed to load targets / gap analysis', err);
      setError('Couldn’t load targets. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [childId, fromDate, toDate]);

  useEffect(() => { load(); }, [load]);

  const gapById = useMemo(() => {
    const m = new Map<number, GapTargetResult>();
    gap?.targets.forEach(t => m.set(t.id, t));
    return m;
  }, [gap]);
  const threshold = gap?.threshold ?? 0.45;

  const startAdd = () => { setForm(EMPTY_FORM); setEditing('new'); };
  const startEdit = (t: EhcpTarget) => {
    setForm({ target_text: t.target_text, area: t.area ?? '', is_active: t.is_active });
    setEditing(t.id);
  };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY_FORM); };

  const save = async () => {
    if (!form.target_text.trim()) return;
    setBusy(true);
    try {
      if (editing === 'new') {
        await EhcpTargetService.create({ childId, target_text: form.target_text.trim(), area: form.area || null });
      } else if (typeof editing === 'number') {
        await EhcpTargetService.update(editing, {
          target_text: form.target_text.trim(),
          area: form.area || null,
          is_active: form.is_active,
        });
      }
      cancelEdit();
      await load();
    } catch (err) {
      console.error('Failed to save target', err);
      setError('Couldn’t save the target.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this target? This cannot be undone.')) return;
    setBusy(true);
    try {
      await EhcpTargetService.remove(id);
      await load();
    } catch (err) {
      console.error('Failed to delete target', err);
      setError('Couldn’t delete the target.');
    } finally {
      setBusy(false);
    }
  };

  const toggleExpand = (id: number) =>
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const renderForm = () => (
    <div className="ci-target-form">
      <textarea
        className="form-control"
        rows={2}
        placeholder="e.g. Will independently transition between activities using a visual timetable"
        value={form.target_text}
        onChange={e => setForm(f => ({ ...f, target_text: e.target.value }))}
      />
      <div className="ci-target-form__row">
        <select
          className="form-select form-select-sm"
          value={form.area}
          onChange={e => setForm(f => ({ ...f, area: e.target.value }))}
        >
          <option value="">SEND area (optional)</option>
          {SEND_DOMAINS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
        </select>
        {typeof editing === 'number' && (
          <label className="ci-target-form__active">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
            /> Active
          </label>
        )}
        <div className="ci-target-form__spacer" />
        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelEdit} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={busy || !form.target_text.trim()}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="ci-targets">
      <div className="ci-targets__head">
        <div>
          <h3 className="ci-targets__title">EHCP targets &amp; evidence</h3>
          <div className="ci-targets__sub">
            Each target is matched against {childName ?? 'the child'}’s published posts by meaning.
            Scores are relative — use “strong matches” and the trend, not the absolute number.
          </div>
        </div>
        <div className="atc-spacer" />
        {canManage && editing == null && (
          <button type="button" className="btn btn-sm btn-primary" onClick={startAdd}>+ Add target</button>
        )}
      </div>

      {error && <div className="alert alert-warning py-2">{error}</div>}
      {editing === 'new' && renderForm()}

      {loading && !gap ? (
        <div className="atc-empty">Loading targets…</div>
      ) : targets.length === 0 && editing !== 'new' ? (
        <div className="atc-empty">
          No EHCP targets yet for {childName ?? 'this child'}.
          {canManage && ' Add the child’s official outcome targets to track evidence against them.'}
        </div>
      ) : (
        <div className="ci-targets__list">
          {targets.map(t => {
            if (editing === t.id) {
              return <div key={t.id} className="ci-target-card">{renderForm()}</div>;
            }
            const g = gapById.get(t.id);
            const verdict = verdictFor(g?.max_score ?? null, threshold);
            const meta = VERDICT_META[verdict];
            const ev = g?.evidence ?? [];
            const isOpen = expanded.has(t.id);
            const shown = isOpen ? ev : ev.slice(0, 3);
            return (
              <div key={t.id} className={`ci-target-card${t.is_active ? '' : ' ci-target-card--inactive'}`}>
                <div className="ci-target-card__top">
                  <div className="ci-target-card__text">
                    {t.area && (
                      <span className="ci-chip" style={{ background: AREA_COLOUR[t.area] ?? '#7e8a78' }}>
                        {AREA_LABEL[t.area] ?? t.area}
                      </span>
                    )}
                    {!t.is_active && <span className="ci-chip ci-chip--muted">Archived</span>}
                    <span>{t.target_text}</span>
                  </div>
                  {canManage && (
                    <div className="ci-target-card__actions">
                      <button type="button" className="btn btn-sm btn-link" onClick={() => startEdit(t)}>Edit</button>
                      <button type="button" className="btn btn-sm btn-link text-danger" onClick={() => remove(t.id)}>Delete</button>
                    </div>
                  )}
                </div>

                {!t.has_embedding ? (
                  <div className="ci-target-card__pending">
                    ⏳ Evidence matching pending — the target’s embedding hasn’t been generated yet
                    (re-save once AI processing is available).
                  </div>
                ) : (
                  <>
                    <div className="ci-target-card__metrics">
                      <div className="ci-confidence">
                        <div className="ci-confidence__bar">
                          <div
                            className="ci-confidence__fill"
                            style={{ width: `${Math.round((g?.max_score ?? 0) * 100)}%`, background: meta.colour }}
                          />
                          <div className="ci-confidence__mark" style={{ left: `${Math.round(threshold * 100)}%` }} title={`Match threshold ${Math.round(threshold * 100)}%`} />
                        </div>
                        <div className="ci-confidence__label" style={{ color: meta.colour }}>
                          {verdict === 'gap' && '⚠ '}{meta.label}
                          {g?.max_score != null && <span className="ci-confidence__pct"> · best {Math.round(g.max_score * 100)}%</span>}
                        </div>
                      </div>
                      <div className="ci-target-card__counts">
                        <strong>{g?.strong_count ?? 0}</strong> strong match{(g?.strong_count ?? 0) === 1 ? '' : 'es'}
                        <span className="ci-muted"> of {g?.posts_considered ?? 0} posts</span>
                      </div>
                      {(g?.trend?.length ?? 0) >= 2 && (
                        <div className="ci-spark" title="Best match per month">
                          <ResponsiveContainer width="100%" height={40}>
                            <LineChart data={g!.trend} margin={{ top: 4, right: 4, bottom: 2, left: 4 }}>
                              <YAxis hide domain={[0, 'dataMax']} />
                              <Tooltip
                                formatter={((v: any) => [`${Math.round(v * 100)}%`, 'Best match']) as any}
                                labelFormatter={(l: any) => l}
                              />
                              <Line type="monotone" dataKey="best" stroke={meta.colour} strokeWidth={2} dot={{ r: 2 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      )}
                    </div>

                    {shown.length > 0 ? (
                      <div className="ci-evidence">
                        {shown.map(e => (
                          <div key={e.post_id} className="ci-evidence__item">
                            <span className="ci-evidence__date">{fmtDate(e.post_date)}</span>
                            <span className="ci-evidence__score">{Math.round(e.score * 100)}%</span>
                            <span className="ci-evidence__snippet">{e.snippet}</span>
                          </div>
                        ))}
                        {ev.length > 3 && (
                          <button type="button" className="btn btn-sm btn-link ci-evidence__more" onClick={() => toggleExpand(t.id)}>
                            {isOpen ? 'Show less' : `Show ${ev.length - 3} more`}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="ci-evidence ci-muted">No matching posts in this period.</div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TargetGapAnalysis;
